import React, { FC, useMemo, useState } from "react";
import { Button, Checkbox, FormControl, TextField } from "@mui/material";
import styles from "./MultiSelectField.module.css";
import { MultiSelectOption } from "./MultiSelectOptions";

interface MultiSelectTransferFieldProps {
  label: string;
  value: Array<number | string>;
  onChange: (value: Array<number | string>) => void;
  options: MultiSelectOption[];
  required?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
}

const normalizeText = (value: string) => value.trim().toLowerCase();

const MultiSelectTransferField: FC<MultiSelectTransferFieldProps> = ({
  label,
  value,
  onChange,
  options,
  fullWidth = true,
  placeholder = "",
  error,
  helperText,
}) => {
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [availableMarked, setAvailableMarked] = useState<Array<number | string>>([]);
  const [selectedMarked, setSelectedMarked] = useState<Array<number | string>>([]);

  const optionIds = useMemo(() => options.map((option) => option.id), [options]);
  const selectedValues = useMemo(
    () => options.filter((option) => value.includes(option.id)),
    [options, value],
  );
  const availableValues = useMemo(
    () => options.filter((option) => !selectedValues.some((selected) => selected.id === option.id)),
    [options, selectedValues],
  );

  const filteredAvailable = useMemo(() => {
    const search = normalizeText(availableSearch);
    return availableValues.filter((option) => normalizeText(option.nome).includes(search));
  }, [availableSearch, availableValues]);

  const filteredSelected = useMemo(() => {
    const search = normalizeText(selectedSearch);
    return selectedValues.filter((option) => normalizeText(option.nome).includes(search));
  }, [selectedSearch, selectedValues]);

  const toggleMarked = (
    targetValue: number | string,
    currentMarked: Array<number | string>,
    setMarked: React.Dispatch<React.SetStateAction<Array<number | string>>>,
  ) => {
    setMarked(
      currentMarked.includes(targetValue)
        ? currentMarked.filter((item) => item !== targetValue)
        : [...currentMarked, targetValue],
    );
  };

  const toggleAllMarked = (
    items: MultiSelectOption[],
    currentMarked: Array<number | string>,
    setMarked: React.Dispatch<React.SetStateAction<Array<number | string>>>,
  ) => {
    const itemIds = items.map((item) => item.id);
    const allMarked = itemIds.length > 0 && itemIds.every((item) => currentMarked.includes(item));
    setMarked(
      allMarked
        ? currentMarked.filter((item) => !itemIds.includes(item))
        : [...new Set([...currentMarked, ...itemIds])],
    );
  };

  const addSelected = () => {
    onChange([
      ...selectedValues.map((item) => item.id),
      ...availableMarked.filter((item) => !selectedValues.some((selected) => selected.id === item)),
    ]);
    setAvailableMarked([]);
  };

  const addAll = () => {
    onChange(optionIds);
    setAvailableMarked([]);
    setSelectedMarked([]);
  };

  const removeSelected = () => {
    onChange(selectedValues.filter((item) => !selectedMarked.includes(item.id)).map((item) => item.id));
    setSelectedMarked([]);
  };

  const removeAll = () => {
    onChange([]);
    setAvailableMarked([]);
    setSelectedMarked([]);
  };

  const allAvailableMarked =
    filteredAvailable.length > 0 &&
    filteredAvailable.every((item) => availableMarked.includes(item.id));
  const allSelectedMarked =
    filteredSelected.length > 0 &&
    filteredSelected.every((item) => selectedMarked.includes(item.id));

  return (
    <FormControl fullWidth={fullWidth}>
      <label className={styles.titleInput}>{label}</label>
      <div className={styles.transferContainer}>
        <div className={styles.transferPanel}>
          <div className={styles.transferHeader}>Opcoes disponiveis</div>
          <TextField
            size="small"
            variant="standard"
            fullWidth
            value={availableSearch}
            onChange={(event) => setAvailableSearch(event.target.value)}
            placeholder={placeholder || "Buscar opcao"}
            error={error}
            helperText={helperText}
            InputProps={{
              className: styles.inputSelect,
              disableUnderline: true,
            }}
          />
          <button
            type="button"
            className={styles.transferSelectAll}
            onClick={() => toggleAllMarked(filteredAvailable, availableMarked, setAvailableMarked)}
          >
            <Checkbox
              size="small"
              checked={allAvailableMarked}
              indeterminate={
                !allAvailableMarked &&
                filteredAvailable.some((item) => availableMarked.includes(item.id))
              }
            />
            Selecionar todos
          </button>
          <div className={styles.transferList}>
            {filteredAvailable.map((option) => (
              <button
                key={option.id}
                type="button"
                className={styles.transferListItem}
                onClick={() => toggleMarked(option.id, availableMarked, setAvailableMarked)}
              >
                <Checkbox size="small" checked={availableMarked.includes(option.id)} />
                <span>{option.nome}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.transferActions}>
          <Button
            variant="contained"
            className={`${styles.transferActionButton} ${availableMarked.length === 0 ? styles.transferActionButtonDisabled : ""}`}
            onClick={addSelected}
            disabled={availableMarked.length === 0}
          >
            Adicionar Selecionado(s) &gt;
          </Button>
          <Button
            variant="contained"
            className={`${styles.transferActionButton} ${availableValues.length === 0 ? styles.transferActionButtonDisabled : ""}`}
            onClick={addAll}
            disabled={availableValues.length === 0}
          >
            Adicionar Tudo &gt;&gt;
          </Button>
          <Button
            variant="contained"
            className={`${styles.transferActionButton} ${selectedMarked.length === 0 ? styles.transferActionButtonDisabled : ""}`}
            onClick={removeSelected}
            disabled={selectedMarked.length === 0}
          >
            &lt; Remover Selecionado(s)
          </Button>
          <Button
            variant="contained"
            className={`${styles.transferActionButton} ${selectedValues.length === 0 ? styles.transferActionButtonDisabled : ""}`}
            onClick={removeAll}
            disabled={selectedValues.length === 0}
          >
            &lt;&lt; Remover Tudo
          </Button>
        </div>

        <div className={styles.transferPanel}>
          <div className={styles.transferHeader}>Opcoes selecionadas</div>
          <TextField
            size="small"
            variant="standard"
            fullWidth
            value={selectedSearch}
            onChange={(event) => setSelectedSearch(event.target.value)}
            placeholder="Buscar selecionadas"
            InputProps={{
              className: styles.inputSelect,
              disableUnderline: true,
            }}
          />
          <button
            type="button"
            className={styles.transferSelectAll}
            onClick={() => toggleAllMarked(filteredSelected, selectedMarked, setSelectedMarked)}
          >
            <Checkbox
              size="small"
              checked={allSelectedMarked}
              indeterminate={
                !allSelectedMarked &&
                filteredSelected.some((item) => selectedMarked.includes(item.id))
              }
            />
            Selecionar todos
          </button>
          <div className={styles.transferList}>
            {filteredSelected.map((option) => (
              <button
                key={option.id}
                type="button"
                className={styles.transferListItem}
                onClick={() => toggleMarked(option.id, selectedMarked, setSelectedMarked)}
              >
                <Checkbox size="small" checked={selectedMarked.includes(option.id)} />
                <span>{option.nome}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {error && helperText ? (
        <span className={styles.transferHelperText}>{helperText}</span>
      ) : null}
    </FormControl>
  );
};

export default MultiSelectTransferField;
